// Standard PageHeader (Spec §6.2) — every content screen opens with this.
// Top-to-bottom: breadcrumb (rendered by AppShell) → title (+ optional status
// chip) → one-line description of what the screen is for → inline ? help →
// exactly one primary action (+ optional secondary) → optional NextStepBanner.
// One primary action per screen makes the next click unambiguous (P1).

import type { ReactNode } from 'react'
import HelpPopover from '../help/HelpPopover'

export interface PageHeaderProps {
  title: string
  /** one-line description of the screen's purpose (slate-600) */
  description?: string
  /** helpContent.ts key for the inline ? affordance */
  helpKey?: string
  /** A screen whose question has no article yet can state the answer inline
   * rather than going without the affordance (Story #5648 build v2, S2). */
  helpTitle?: string
  helpBody?: string
  /** optional status chip beside the title */
  status?: { label: string; tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' }
  /** the single primary action (right-aligned) */
  primaryAction?: ReactNode
  /** at most one or two secondary actions */
  secondaryActions?: ReactNode
  /** workflow next-step banner rendered immediately below the header */
  banner?: ReactNode
}

const TONE: Record<NonNullable<NonNullable<PageHeaderProps['status']>['tone']>, string> = {
  neutral: 'bg-slate-100 text-slate-600',
  success: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-800',
  danger: 'bg-rose-100 text-rose-700',
  info: 'bg-iflow-100 text-iflow-700',
}

export default function PageHeader({
  title,
  description,
  helpKey,
  helpTitle,
  helpBody,
  status,
  primaryAction,
  secondaryActions,
  banner,
}: PageHeaderProps) {
  return (
    <div className="mb-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-2xl font-bold tracking-tight text-iflow-900">{title}</h1>
            {status && (
              <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE[status.tone ?? 'neutral']}`}>
                {status.label}
              </span>
            )}
            {(helpKey || helpBody) && (
              <HelpPopover topic={helpKey ?? 'screen'} title={helpTitle} body={helpBody} size={18} />
            )}
          </div>
          {description && <p className="mt-1 max-w-2xl text-sm text-slate-600">{description}</p>}
        </div>
        {(primaryAction || secondaryActions) && (
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
            {secondaryActions}
            {primaryAction}
          </div>
        )}
      </div>
      {banner && <div className="mt-4">{banner}</div>}
    </div>
  )
}
